import { TAutocompleteOption } from '~/components/AutocompleteField/types';
import { TAddress, TPersonalDocument } from '../../types';
import type { CollisionDetector } from '@dnd-kit/abstract';

export type TTaxInfo = {
  identificationNumber: string | null;
  foreignTaxNumber: string | null;
};

export type TCountryInfo = {
  birthPlace: string | null;
  citizenshipCountry: TAutocompleteOption | null;
  birthplaceCountry: TAutocompleteOption | null;
  residenceCountry: TAutocompleteOption | null;
};

export type TPersonalInfo = {
  firstName: string | null;
  lastName?: string | null;
  middleName: string | null;
  birthDate: string | null;
};

export type TOwnershipStructure = {
  owners: TOwner[];
  isBeneficiariesAbsent?: boolean;
  isFoundersAndBeneficiariesAbsent: boolean;
  ownershipPercentageTotal: string | number | null;
  link: TOwnerLink[];
  ownershipStructureSpecifics: string | null;
};

export type TDocumentType = {
  id: number;
  name: string;
};

export enum OwnerType {
  INDIVIDUAL = 'INDIVIDUAL',
  LEGAL_ENTITY = 'LEGAL_ENTITY',
  SHAREHOLDER_GROUP = 'SHAREHOLDER_GROUP',
  FREE_FLOAT = 'FREE_FLOAT',
  BENEFICIARY_WITHOUT_FORMAL_OWNERSHIP = 'BENEFICIARY_WITHOUT_FORMAL_OWNERSHIP',
}

export type TOwner = {
  id: string;
  type: OwnerType;
  address: TAddress | null;
  documents: TPersonalDocument | null;
  ownershipPercentage: string | number | null;
  isInTerList: boolean;
  isBeneficiary: boolean | null;
  isUSTaxResident: boolean;
  isInBankBlackList: boolean;
  isOtherTaxResident: boolean;
  linksLabels: string[] | null;
  fullName: string | null;
  shortNameUkr: string | null;
  rangeLegal: TOwnerRange | null;
  rangeIndividual: TOwnerRange | null;
  sourceURL: string | null;
  registrationDate: string | null;
  typesOfActivity: TAutocompleteOption | null;
  hasRussianOrBelarusianCitizenShareholders: boolean | null;
  isRegardlessOfFormalOwnership: boolean | null;
  areIndividualsWithDecisiveInfluenceAbsent: boolean | null;
  russianBelarusianIndividuals: TForbidenCountryIndividual[] | null;
  russianBelarusianLegalEntities: TForbidenCountryLegalEntity[] | null;
} & TPersonalInfo &
  TCountryInfo &
  TTaxInfo;

export type TOwnerRange = {
  number: string | number | null;
  from: string | number | null;
  to: string | number | null;
};

export type TImportOwnerList = { [key: number]: TAutocompleteOption | null };

export type TOwnerLink = {
  id: string;
  fromNodeId: string;
  toNodeId: string;
  ownershipPercentage: number | string | null;
  parentLinkId: string | null;
  displayOrder: number;
};

export type TForbidenCountryIndividual = {
  fullName: string | null;
  residencyStatus: TAutocompleteOption | null;
  citizenshipStatus: TAutocompleteOption | null;
  ownershipShare: number | null;
};

export type TForbidenCountryLegalEntity = {
  entityName: string | null;
  residencyStatus: TAutocompleteOption | null;
  ownershipShare: number | null;
};

export type TOwnerTreeNode = {
  [key: string]: TOwnerNode;
};

export type TOwnerNode = TOwner & {
  fieldIndex: number;
  linkIndex: number;
  children: TOwnerNode[];
  linkId: string;
  parentLinkId: string | null;
  ancestorLinkIds: string[];
  isCyclic: boolean;
  level?: number;
};

export type TBandInput = Parameters<CollisionDetector>[0];

export type TNodeCardData = {
  ownerType: string;
  level: number;
  height?: number;
  fullName?: string | null;
  birthDate?: string | null;
  identificationNumber?: string | null;
  foreignTaxNumber?: string | null;
  citizenshipCountry?: TAutocompleteOption | null;
  address?: TAddress;
  isBeneficiary?: boolean;
  shortNameUkr?: string | null;
  sourceURL?: string | null;
  ownershipPercentage?: number | null;
  isRoot?: boolean;
  rangeIndividual?: TRange;
  rangeLegal?: TRange;
  hasRussianOrBelarusianCitizenShareholders?: boolean;
  russianBelarusianIndividuals?: TForbidenCountryIndividual[] | null;
  russianBelarusianLegalEntities?: TForbidenCountryLegalEntity[] | null;
  areIndividualsWithDecisiveInfluenceAbsent?: boolean | null;
  isDuplicate?: boolean;
};

export type TOwnerDiagramNode = {
  level: number;
  id: string;
  type: OwnerType;
  fullName: string | null;
  birthDate?: string | null;
  identificationNumber?: string | null;
  foreignTaxNumber?: string | null;
  citizenshipCountry?: TAutocompleteOption | null;
  ownershipPercentage: string | number | null;
  isBeneficiary?: boolean | null;
  shortNameUkr?: string | null;
  address?: TAddress;
  sourceURL?: string | null;
  lastName?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  rangeIndividual?: TRange;
  rangeLegal?: TRange;
  children?: TOwnerDiagramNode[];
  hasRussianOrBelarusianCitizenShareholders?: boolean;
  russianBelarusianIndividuals?: TForbidenCountryIndividual[] | null;
  russianBelarusianLegalEntities?: TForbidenCountryLegalEntity[] | null;
  areIndividualsWithDecisiveInfluenceAbsent?: boolean | null;
  isDuplicate?: boolean;
};

export type TRange = {
  from: number | null;
  number: number | null;
  to: number | null;
};
export type TDropZoneProps = {
  isRoot?: boolean;
  isOver?: boolean;
  isDisabled?: boolean;
};
export interface INodeCardProps {
  data: TNodeCardData;
}

export type TGenerateOwnershipStructurePdfParams = {
  owners: TOwner[];
  link: TOwnerLink[];
  fullNameUkr: string;
  identificationNumber: string;
  clientFullInformation: string;
};

export type TTextSegment = { text: string; bold: boolean };

export type TOwnerTypeStateAction = React.Dispatch<
  React.SetStateAction<
    | {
        [key: number]: TAutocompleteOption | null;
      }
    | undefined
  >
>;
